/**
 * Centralized AI configuration system
 * 
 * REFACTORED APPROACH:
 * - All components normalized to [0-2] or [-2, +2]
 * - Final utility = normalized * CV * weight
 * - CV (CREATURE_VALUE) as universal denominator
 * - Weights express component importance in "creature equivalents"
 */

import { StrategicStance } from "@/core/battle/ai/types/ai.types";

export interface AIWeights {
    // ========================================
    // ATTACK WEIGHTS
    // ========================================
    DAMAGE_WEIGHT: number;              // Multiplier for damage dealt
    TARGET_VALUE_WEIGHT: number;        // Multiplier for target bonuses (oneshot, strong/weak)
    FOCUS_FIRE_BONUS: number;          // Multiplier for wounded targets
    
    // ========================================
    // MOVEMENT WEIGHTS
    // ========================================
    CLOSING_WEIGHT: number;             // Approaching enemy (can be negative for retreat)
    PATH_ALIGNMENT_BONUS: number;       // Following strategic paths
    ENTERING_RANGE_WEIGHT: number;      // Entering attack range
    REPOSITIONING_WEIGHT: number;       // Moving within attack range
    
    // ========================================
    // RISK WEIGHTS
    // ========================================
    RISK_WEIGHT: number;                // Threat delta (change in danger level)
    THREAT_ZONE_WEIGHT: number;         // Absolute threat level penalty
    OPPORTUNITY_RISK_WEIGHT: number;    // Opportunity attacks (interpreted as hit probability)

    // ========================================
    // IN RANGE WEIGHTS
    // ========================================
    POSITION_DISTANCE_WEIGHT: number;
    FATIGUE_BONUS_WEIGHT: number;
    
    // ========================================
    // SPECIAL WEIGHTS
    // ========================================
    MOVE_COST_WEIGHT: number;           // "Tax" on movement (prevents meaningless moves)
    KITING_WEIGHT: number;              // Ranged retreat bonus
    GOAL_BONUS: number;

    DISCIPLINE: number;
    MIN_SAFETY: number;
    
    // ========================================
    // STANCE CONFIGURATION
    // ========================================
    THREAT_ZONE_TARGET: number;         // Desired threat level (stance-dependent)
}

export const STANCE_THRESHOLDS = {
    AGGRESSIVE_RATIO: 1.3,
    DEFENSIVE_RATIO: 0.8,
} as const;

/**
 * Utility calculation constants
 * All normalized values expressed as multiples of CREATURE_VALUE
 */
export const UTILITY_CONSTANTS = {
    // ========================================
    // BASE UNIT
    // ========================================
    CREATURE_VALUE: 10,  // 1 creature killed = 10 points
    
    // ========================================
    // ATTACK BONUSES
    // ========================================
    ONESHOT_BONUS: 3,              // Eliminating entire stack
    STRONG_TARGET_BONUS: 1.5,      // Killing strong enemy
    WEAK_TARGET_BONUS: 0.5,        // Killing weak enemy
    NORMAL_TARGET_BONUS: 1,        // Normal target
    WOUNDED_BONUS_MAX: 2,          // Max bonus for wounded target
    
    // ========================================
    // OPPORTUNITY RISK
    // ========================================
    INTERRUPT_PENALTY_BASE: 5,   // Cost of losing turn (in creature equivalents)
    
    // ========================================
    // NORMALIZATION PARAMETERS
    // ========================================
    CLOSING_DISTANCE_DIVISOR: 3,   // Normalize closing distance (steps/3 → [0-2])
    PATH_LOG_BASE: 11,             // Logarithmic normalization for path priority
    
} as const;

/**
 * AGGRESSIVE Stance
 * - High closing weight (rush to enemy)
 * - Low risk aversion (willing to enter red zones)
 * - Low opportunity risk weight (50% hit probability assumption)
 * - Target threat: 1.5x stackHP (comfortable in red zone)
 */
const AGGRESSIVE_WEIGHTS: AIWeights = {
    // Attack
    DAMAGE_WEIGHT: 1.4,
    TARGET_VALUE_WEIGHT: 1.2,
    FOCUS_FIRE_BONUS: 1.5,
    
    // Movement
    CLOSING_WEIGHT: 1.2,           // Strong pull to enemy
    PATH_ALIGNMENT_BONUS: 1.15,
    ENTERING_RANGE_WEIGHT: 0.35,
    REPOSITIONING_WEIGHT: 0.8,
    
    // Risk
    
    THREAT_ZONE_WEIGHT: 0.8,       // Very low absolute threat penalty
    OPPORTUNITY_RISK_WEIGHT: 0.5,  // 50% hit probability (willing to risk)
    
    // Special
    MOVE_COST_WEIGHT: 0.3,         // Low movement tax
    KITING_WEIGHT: 0.5,            // Ranged rarely retreats
    GOAL_BONUS: 1.5,

    RISK_WEIGHT: 0.3,              // Standard threat aversion
    DISCIPLINE: 0.1,
    MIN_SAFETY: 0.3,
    
    // in range
    POSITION_DISTANCE_WEIGHT: 0.40,
    FATIGUE_BONUS_WEIGHT: 0.15,

    // Target zone
    THREAT_ZONE_TARGET: 1.5,       // Comfortable at 1.5x stackHP threat (red zone)
};

/**
 * BALANCED Stance
 * - Moderate closing weight
 * - Balanced risk/reward
 * - 80% hit probability for opp attacks
 * - Target threat: 0.8x stackHP (yellow zone)
 */
const BALANCED_WEIGHTS: AIWeights = {
    // Attack
    DAMAGE_WEIGHT: 1.3,
    TARGET_VALUE_WEIGHT: 0.8,
    FOCUS_FIRE_BONUS: 1,
    
    // Movement
    CLOSING_WEIGHT: 1,           // Moderate pull
    PATH_ALIGNMENT_BONUS: 1,
    ENTERING_RANGE_WEIGHT: 0.25,
    REPOSITIONING_WEIGHT: 0.5,
    
    // Risk
    
    THREAT_ZONE_WEIGHT: 1,       // Moderate absolute threat penalty
    OPPORTUNITY_RISK_WEIGHT: 0.95,  // 80% hit probability
    
    // Special
    MOVE_COST_WEIGHT: 1,         // Moderate movement tax
    KITING_WEIGHT: 1.0,
    GOAL_BONUS: 0.5,

    RISK_WEIGHT: 0.75,              // Standard threat aversion
    DISCIPLINE: 0.1,
    MIN_SAFETY: 0.05,

    // in range
    POSITION_DISTANCE_WEIGHT: 0.40,
    FATIGUE_BONUS_WEIGHT: 0.15,
    
    // Target zone
    THREAT_ZONE_TARGET: 1,       // Prefers yellow zone (0.8x stackHP)
};

/**
 * DEFENSIVE Stance
 * - Low closing weight (cautious approach)
 * - High risk aversion (avoid dangerous zones)
 * - 100% hit probability for opp attacks
 * - Target threat: 0.3x stackHP (green zone)
 */
const DEFENSIVE_WEIGHTS: AIWeights = {
    // Attack
    DAMAGE_WEIGHT: 1.0,
    TARGET_VALUE_WEIGHT: 0.8,
    FOCUS_FIRE_BONUS: 1.0,
    
    // Movement
    CLOSING_WEIGHT: 0.7,           // Weak pull (cautious)
    PATH_ALIGNMENT_BONUS: 0.8,
    ENTERING_RANGE_WEIGHT: 0.2,
    REPOSITIONING_WEIGHT: 0.3,
    
    // Risk
    
    THREAT_ZONE_WEIGHT: 2.0,       // Strong absolute threat penalty
    OPPORTUNITY_RISK_WEIGHT: 1.0,  // 100% hit probability (very cautious)
    
    // Special
    MOVE_COST_WEIGHT: 1.5,         // High movement tax (prefers standing)
    KITING_WEIGHT: 1.5,            // Strong kiting preference
    GOAL_BONUS: 0.2,

    RISK_WEIGHT: 1,              // Standard threat aversion
    DISCIPLINE: 0.2,
    MIN_SAFETY: 0.1,

    // in range
    POSITION_DISTANCE_WEIGHT: 0.40,
    FATIGUE_BONUS_WEIGHT: 0.15,
    
    // Target zone
    THREAT_ZONE_TARGET: 0.3,       // Stays in green zone (0.3x stackHP)
};

export function getStanceWeights(stance: StrategicStance): AIWeights {
    switch (stance) {
        case 'AGGRESSIVE':
            return AGGRESSIVE_WEIGHTS;
        case 'DEFENSIVE':
            return DEFENSIVE_WEIGHTS;
        case 'BALANCED':
        default:
            return BALANCED_WEIGHTS;
    }
}

// Behavior detection thresholds
export const UNIT_BEHAVIOR = {
    MELEE_THRESHOLD: 2,             // range <= this = melee
    RANGED_MIN_DISTANCE: 4,         // ranged units try to stay at least this far
    RANGED_PREFERRED_DISTANCE: 6,   // ranged units prefer this distance
};

export const PLANNING_CONFIG = {
    maxDepth: 3,
    pruneThreshold: 0.5,
    maxZeroCandidates: 91,
    enablePathBased: true,
    enableOpportunistic: true,
    debug: __PLANNING_DEBUG__,
} as const;